import {DestroyRef, Injectable, computed, inject, signal} from '@angular/core';
import {takeUntilDestroyed, toObservable} from '@angular/core/rxjs-interop';
import {EMPTY, Observable, catchError, expand, finalize, forkJoin, map, of, reduce, shareReplay, switchMap, tap} from 'rxjs';
import {ManagementService} from './management.service';
import {
  Account,
  AccountPayload,
  AccountSummary,
  AiPrompt,
  AiTopicRow,
  Brand,
  BrandPayload,
  BrandSummary,
  ManagementRequest,
  SortState,
  Topic,
  User,
  UserPayload,
} from './types';

export type TableRow = Account | User | Brand | AiTopicRow;

interface TableQuery {
  tab: string;
  page: number;
  pageSize: number;
  sortBy: string;
  sortDir: 'asc' | 'desc';
  term: string;
}

interface TableResult {
  rows: TableRow[];
  total: number;
}

export type MetaPageCountry = { code: string; name: string };

const PAGE_SIZE: Record<string, number> = {accounts: 10, users: 10, brands: 15, 'ai-presence': 15};

// The prompts endpoint pages at 500/request (server @Max(500)); this caps the request chain.
// 20 pages = 10,000 prompts. Prod holds 1,788 today and gained 200 in one day on 2026-09-08,
// so 4 pages (2,000) was within 12% of live volume. Past the cap the screen says it is partial
// rather than silently under-counting.
const PROMPTS_PAGE_LIMIT = 500;
const PROMPTS_MAX_PAGES = 20;

/**
 * Single source of truth for the management screen. Owns:
 *  - the active table's query state (tab/page/sort/term) and its rows/total/loading/error,
 *  - cross-tab reference data for the drawers' dropdowns, lazily fetched once and cached for the
 *    session (so revisiting the screen or reopening a drawer doesn't refetch),
 *  - every list mutation, each of which does exactly one refresh + the matching cache invalidation.
 *
 * Loading is a LOCAL signal here — the management screen no longer touches the global StateService
 * spinner, so there is no cross-component counter to keep balanced.
 */
@Injectable({providedIn: 'root'})
export class ManagementStore {
  private readonly api = inject(ManagementService);
  private readonly destroyRef = inject(DestroyRef);

  // --- query state ---
  private readonly _tab = signal('');
  private readonly _page = signal(0);
  private readonly _sortBy = signal('');
  private readonly _sortDir = signal<'asc' | 'desc'>('desc');
  private readonly _term = signal('');
  private readonly _reloadTrigger = signal(0);

  // --- table view state ---
  private readonly _rows = signal<TableRow[]>([]);
  private readonly _total = signal(0);
  private readonly _loading = signal(false);
  private readonly _error = signal(false);
  // True when the prompt fetch hit the page cap: counts, the drawer's list and "pipeline only"
  // detection are then based on a partial set, so the screen has to say so.
  private readonly _promptsTruncated = signal(false);

  readonly activeTab = this._tab.asReadonly();
  readonly rows = this._rows.asReadonly();
  readonly total = this._total.asReadonly();
  readonly loading = this._loading.asReadonly();
  readonly error = this._error.asReadonly();
  readonly promptsTruncated = this._promptsTruncated.asReadonly();
  readonly page = this._page.asReadonly();
  readonly term = this._term.asReadonly();
  readonly pageSize = computed(() => PAGE_SIZE[this._tab()] ?? PAGE_SIZE['accounts']);

  private readonly query = computed<TableQuery>(() => {
    this._reloadTrigger(); // re-run the fetch on an explicit reload (e.g. after a mutation), even if params are unchanged
    return {
      tab: this._tab(),
      page: this._page(),
      pageSize: this.pageSize(),
      sortBy: this._sortBy(),
      sortDir: this._sortDir(),
      term: this._term(),
    };
  });

  // --- reference data (lazy + cached) ---
  private readonly _brandSummaries = signal<BrandSummary[]>([]);
  private readonly _accountSummaries = signal<AccountSummary[]>([]);
  private readonly _roles = signal<string[]>([]);
  private readonly _aiTopics = signal<Topic[]>([]);

  readonly brandSummaries = this._brandSummaries.asReadonly();
  readonly accountSummaries = this._accountSummaries.asReadonly();
  readonly roles = this._roles.asReadonly();
  readonly aiTopics = this._aiTopics.asReadonly();

  private brandSummaries$?: Observable<BrandSummary[]>;
  private accountSummaries$?: Observable<AccountSummary[]>;
  private roles$?: Observable<string[]>;
  private aiTopics$?: Observable<Topic[]>;
  private allAiPrompts$?: Observable<AiPrompt[]>;
  private metaPageCountries$?: Observable<MetaPageCountry[]>;

  constructor() {
    // One cancelable load pipeline. switchMap cancels a stale request when the query changes;
    // finalize keeps the loading flag balanced even on cancellation; catchError surfaces the
    // failure without tearing down the stream, so retries keep working.
    toObservable(this.query)
      .pipe(
        switchMap(query => {
          if (!query.tab) {
            return of(null);
          }
          this._loading.set(true);
          this._error.set(false);
          return this.fetchFor(query).pipe(
            // No logging here — ManagementService already logs every failed request before rethrowing.
            catchError(() => {
              this._error.set(true);
              return of(null);
            }),
            finalize(() => this._loading.set(false)),
          );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(result => {
        if (result) {
          this._rows.set(result.rows);
          this._total.set(result.total);
        }
      });
  }

  // --- query actions (called by the screen presenter) ---

  setTab(tab: string): void {
    if (tab === this._tab()) {
      return;
    }
    this._tab.set(tab);
    this.resetTabState();
    // Claim loading synchronously so the empty-state overlay can't flash between rows being cleared
    // and the (async) fetch pipeline flipping the flag. finalize() will clear it when the fetch settles.
    this._loading.set(true);
  }

  setPage(pageIndex: number): void {
    this._page.set(pageIndex);
  }

  setSort(sort: SortState): void {
    this._sortBy.set(sort.sortBy);
    this._sortDir.set(sort.sortDir);
  }

  setSearch(term: string): void {
    this._page.set(0);
    this._term.set(term ?? '');
  }

  reload(): void {
    this._reloadTrigger.update(value => value + 1);
  }

  private resetTabState(): void {
    this._page.set(0);
    this._sortBy.set('');
    this._sortDir.set('desc');
    this._term.set('');
    // Clear immediately so the newly-shown table never renders the previous tab's rows (different
    // shape) against its columns. Page/sort/search within a tab keep the rows for a smooth update.
    this._rows.set([]);
    this._total.set(0);
    this._error.set(false);
  }

  private fetchFor(query: TableQuery): Observable<TableResult> {
    const params: Partial<ManagementRequest> = {
      limit: query.pageSize,
      offset: query.page * query.pageSize,
      term: query.term,
      sortBy: query.sortBy,
      sortDir: query.sortDir,
    };
    switch (query.tab) {
      case 'users':
        return this.api.getUsers(params).pipe(map(res => ({rows: res.users as TableRow[], total: res.total})));
      case 'brands':
        return this.api.getBrands({...params, elaborate: true}).pipe(map(res => ({rows: res.brands as TableRow[], total: res.total})));
      case 'accounts':
        return this.api.getAccounts({...params, elaborate: true}).pipe(map(res => ({rows: res.accounts as TableRow[], total: res.total})));
      // Topics + prompts both arrive unpaginated (topics) / fully paged-through (prompts) from small
      // admin-scale endpoints, so search/sort/pagination are done here, client-side. Topics live in
      // console MySQL while prompts are proxied to the interaction layer — a prompts failure (proxy
      // down/undeployed) must degrade the count columns, not blank the whole table.
      case 'ai-presence':
        return forkJoin({
          topics: this.aiTopicsSource(),
          prompts: this.allAiPromptsSource().pipe(catchError(() => of(null))),
        }).pipe(map(({topics, prompts}) => this.buildAiTopicRows(topics, prompts, query)));
      default:
        return of({rows: [], total: 0});
    }
  }

  private buildAiTopicRows(topics: Topic[], prompts: AiPrompt[] | null, query: TableQuery): TableResult {
    const counts = new Map<string, number>();
    for (const prompt of prompts ?? []) {
      const key = prompt.topic.toLowerCase();
      counts.set(key, (counts.get(key) ?? 0) + 1);
    }
    // Prompts whose topic matches no console topic (e.g. seeded straight into Snowflake) would
    // otherwise be invisible — surface them as unmanaged "pipeline only" rows so they can still be
    // inspected and deleted. Creating a console topic with the same name adopts them.
    const knownTopics = new Set(topics.map(topic => topic.name.toLowerCase()));
    const orphanNames = new Map<string, string>(); // lowercase -> first-seen casing
    for (const prompt of prompts ?? []) {
      const key = prompt.topic.toLowerCase();
      if (!knownTopics.has(key) && !orphanNames.has(key)) {
        orphanNames.set(key, prompt.topic);
      }
    }
    const term = query.term.trim().toLowerCase();
    const dir = query.sortDir === 'asc' ? 1 : -1;
    const rows = [
      ...topics.map<AiTopicRow>(topic => ({
        // prompts === null (fetch failed) renders as "—", not a misleading 0
        ...topic, promptCount: prompts ? counts.get(topic.name.toLowerCase()) ?? 0 : null, managed: true,
      })),
      ...[...orphanNames.values()].map<AiTopicRow>(name => ({
        // pipeline-only topics never collect: collection is driven by console brand attachments
        id: '', name, brandKeys: [], collecting: false, promptCount: counts.get(name.toLowerCase()) ?? 0, managed: false,
      })),
    ]
      .filter(topic => !term || topic.name.toLowerCase().includes(term))
      // Only the name column is sortable (the rest are derived counts); default to A→Z.
      .sort((a, b) => (query.sortBy === 'name' ? dir : 1) * a.name.localeCompare(b.name));
    const start = query.page * query.pageSize;
    return {rows: rows.slice(start, start + query.pageSize), total: rows.length};
  }

  private replaceRow(match: (row: TableRow) => boolean, updated: TableRow): void {
    const rows = this._rows();
    const index = rows.findIndex(match);
    if (index === -1) {
      return;
    }
    const next = [...rows];
    next[index] = updated;
    this._rows.set(next);
  }

  // --- reference data loaders ---
  // Each slice caches its (shared) HTTP observable so concurrent callers dedupe and a second drawer
  // open hits the cache. refresh*() busts the cache after a mutation that can change the list.

  ensureBrandSummaries(): void {
    this.brandSummaries$ ??= this.api.getBrands({elaborate: false}).pipe(
      map(res => res.brands as BrandSummary[]),
      catchError(() => of([] as BrandSummary[])),
      shareReplay({bufferSize: 1, refCount: false}),
    );
    this.brandSummaries$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(list => this._brandSummaries.set(list));
  }

  // Bust the cache only — the list is re-fetched lazily the next time a drawer needs it, rather than
  // eagerly here (nothing is reading it while no drawer is open).
  private invalidateBrandSummaries(): void {
    this.brandSummaries$ = undefined;
  }

  ensureAccountSummaries(): void {
    this.accountSummaries$ ??= this.api.getAccounts({elaborate: false}).pipe(
      map(res => res.accounts as AccountSummary[]),
      catchError(() => of([] as AccountSummary[])),
      shareReplay({bufferSize: 1, refCount: false}),
    );
    this.accountSummaries$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(list => this._accountSummaries.set(list));
  }

  private invalidateAccountSummaries(): void {
    this.accountSummaries$ = undefined;
  }

  ensureRoles(): void {
    this.roles$ ??= this.api.getUserRoles().pipe(
      map(res => res.roles),
      catchError(() => of([] as string[])),
      shareReplay({bufferSize: 1, refCount: false}),
    );
    this.roles$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(list => this._roles.set(list));
  }

  // The AI Presence tab mutates topics — bust the cached list so both the table pipeline and the
  // brand drawer's topic dropdown refetch.
  invalidateAiTopics(): void {
    this.aiTopics$ = undefined;
  }

  ensureAiTopics(): void {
    this.aiTopicsSource()
      .pipe(catchError(() => of([] as Topic[])), takeUntilDestroyed(this.destroyRef))
      .subscribe(list => this._aiTopics.set(list));
  }

  // Shared cached source: the ai-presence table fetch and the drawers' dropdowns dedupe on it.
  // A failure drops the cache (shareReplay would otherwise replay the error to every retry).
  private aiTopicsSource(): Observable<Topic[]> {
    this.aiTopics$ ??= this.api.getAiTopics().pipe(
      map(res => res.topics),
      tap({error: () => (this.aiTopics$ = undefined)}),
      shareReplay({bufferSize: 1, refCount: false}),
    );
    return this.aiTopics$;
  }

  invalidateAiPrompts(): void {
    this.allAiPrompts$ = undefined;
  }

  /**
   * Every prompt, paged through the 500/request endpoint and cached. Feeds the topics table's prompt
   * counts and the topic drawer's per-topic prompt list. Errors propagate (the table pipeline shows
   * its shared error overlay; the drawer surfaces a snackbar) and drop the cache so a retry refetches.
   */
  allAiPromptsSource(): Observable<AiPrompt[]> {
    // Reset INSIDE the cache miss: on a hit the tap below never re-runs, so resetting on every
    // call would clear the truncation banner the moment any drawer re-opens.
    if (!this.allAiPrompts$) {
      this._promptsTruncated.set(false);
    }
    this.allAiPrompts$ ??= this.api.getAiPrompts(1, PROMPTS_PAGE_LIMIT).pipe(
      expand(page => (page.page * page.limit < page.total && page.page < PROMPTS_MAX_PAGES ? this.api.getAiPrompts(page.page + 1, PROMPTS_PAGE_LIMIT) : EMPTY)),
      // Runs on every page (after expand): the last one tells us whether rows were left behind.
      tap(page => this._promptsTruncated.set(page.page >= PROMPTS_MAX_PAGES && page.page * page.limit < page.total)),
      reduce((all, page) => all.concat(page.data), [] as AiPrompt[]),
      tap({error: () => (this.allAiPrompts$ = undefined)}),
      shareReplay({bufferSize: 1, refCount: false}),
    );
    return this.allAiPrompts$;
  }

  // Static, brand-scoped list — cached for the session and shared across brand-drawer opens. Returned
  // as an observable (rather than a signal) because each consumer maps it differently (e.g. flag URLs).
  getMetaPageCountries(): Observable<MetaPageCountry[]> {
    this.metaPageCountries$ ??= this.api.getMetaPageCountries().pipe(
      catchError(() => of([] as MetaPageCountry[])),
      shareReplay({bufferSize: 1, refCount: false}),
    );
    return this.metaPageCountries$;
  }

  // --- mutations (the only place list side-effects live) ---
  // Each returns the repo result so the drawer can show its snackbar / close; the tap performs the
  // single list refresh + the matching reference-cache invalidation.

  addBrand(payload: BrandPayload): Observable<Brand> {
    return this.api.addBrand(payload).pipe(
      tap(result => {
        // Filter the table to the just-added brand (matches the previous UX) and refresh the
        // competitor dropdown's source.
        this._term.set(result.name);
        this._page.set(0);
        this.reload();
        this.invalidateBrandSummaries();
      }),
    );
  }

  editBrand(payload: BrandPayload): Observable<Brand> {
    return this.api.editBrand(payload).pipe(
      tap(result => {
        this.replaceRow(row => (row as Brand).brandKey === result.brandKey, result);
        this.invalidateBrandSummaries(); // name can change
      }),
    );
  }

  addAccount(payload: AccountPayload): Observable<Account> {
    return this.api.addAccount(payload).pipe(
      tap(() => {
        this._term.set('');
        this._page.set(0);
        this.reload();
        this.invalidateAccountSummaries();
      }),
    );
  }

  editAccount(payload: AccountPayload): Observable<Account> {
    return this.api.editAccount(payload).pipe(
      tap(result => {
        this.replaceRow(row => (row as Account).accountKey === result.accountKey, result);
        this.invalidateAccountSummaries(); // name / brands feed the user drawer's account dropdown
      }),
    );
  }

  updateAccountSubscription(accountKey: string, payload: { action?: 'convert'; trialEndsAt?: string }): Observable<Account> {
    return this.api.updateAccountSubscription(accountKey, payload).pipe(
      tap(result => this.replaceRow(row => (row as Account).accountKey === result.accountKey, result)),
    );
  }

  deleteAccount(accountKey: string): Observable<Account> {
    return this.api.deleteAccount(accountKey).pipe(
      tap(() => {
        this.reload();
        this.invalidateAccountSummaries();
      }),
    );
  }

  addUser(payload: UserPayload): Observable<User> {
    return this.api.addUser(payload).pipe(
      tap(() => {
        this._term.set('');
        this._page.set(0);
        this.reload();
      }),
    );
  }

  editUser(payload: UserPayload): Observable<User> {
    return this.api.editUser(payload).pipe(
      tap(result => {
        this.replaceRow(row => (row as User).id === result.id, result);
      }),
    );
  }

  deleteUser(userId: string): Observable<unknown> {
    return this.api.deleteUser(userId).pipe(
      tap(() => {
        this.reload();
      }),
    );
  }

  // --- AI Presence mutations ---

  addAiTopic(name: string): Observable<Topic> {
    return this.api.createAiTopic(name).pipe(
      tap(result => {
        // Filter the table to the just-added topic, matching the add-brand UX.
        this._term.set(result.name);
        this._page.set(0);
        this.invalidateAiTopics();
        this.reload();
      }),
    );
  }

  renameAiTopic(id: string, name: string): Observable<Topic> {
    return this.api.updateAiTopic(id, {name}).pipe(
      tap(() => {
        this.invalidateAiTopics();
        this.invalidateAiPrompts(); // prompt rows carry the topic name
        this.reload();
      }),
    );
  }

  deleteAiTopic(id: string): Observable<Topic> {
    return this.api.deleteAiTopic(id).pipe(
      tap(() => {
        this.invalidateAiTopics();
        this.invalidateAiPrompts();
        this.reload();
      }),
    );
  }

  addAiPrompts(items: {topic: string; promptText: string}[]): Observable<AiPrompt[]> {
    return this.api.createAiPrompts(items).pipe(
      tap(() => {
        this.invalidateAiPrompts();
        this.reload(); // the topics table shows prompt counts
      }),
    );
  }

  deleteAiPrompt(promptId: string): Observable<{deleted: number}> {
    return this.api.deleteAiPrompt(promptId).pipe(
      tap(() => {
        this.invalidateAiPrompts();
        this.reload(); // prompt-count column
      }),
    );
  }
}
